import type { Virtualizer } from '@tanstack/react-virtual'
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import type { EnhancedProductFamilyId, EnhancedProductGroup, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import { appRoutes } from '#app/navigation/routes/routes'
import { useAppNavigation } from '#app/navigation/useAppNavigation'
import { type AppDispatch, useAppDispatch, useAppSelector } from '#app/store'
import type { Addition, Change, Removal } from '#app/store/builders/configChangeTracker'
import {
  type Filter,
  filterProductGroups,
  makeDefaultFilterOperators,
  makeGroupIsCollapsedOperator,
  makeGroupNotPartOfWellKnownOperator,
  productVariantContainsString,
  productVariantHasInsights,
} from '#app/store/hooks/useFilteredProductGroups'
import {
  addViewHighlight,
  checkGroupCompatibility,
  clearViewHighlight,
  type LocalConfigurationId,
  setCollapsedGroups,
  setPrintOptions,
  setScrollPosition,
  setVariantForNationalCodeFamily,
  setVariantsForFamily,
  type TruckConfiguration,
  toggleLocked,
  type ViewDetailKey,
} from '#app/store/slices/configurationSlice'
import type { ConfigurationEnrichmentData } from '#app/store/slices/enrichConfiguration'
import { useTableChangeHighlightEnabled } from '#app/store/slices/featureSlice'
import { toggleExpandPrintColumns } from '#app/store/slices/localConfigSlice'
import { toggleFavorite } from '#app/store/slices/userDataSlice'
import type { AppProductGroup, ProductPath } from '#app/types/AppTypes'
import type { PrintOptions } from '#features/PrintOptions/PrintOptions'
import type { EntryHighlight } from '#features/VirtualizedGrid'
import { isProductFamily, isProductGroup, isProductVariant } from '#utils/EntryTypeGuards'
import { getHierarchyPaths } from '#utils/getHierarchyPaths'
import { matchCodeGroups } from '#utils/matchCodeGroups'
import { useHoveredEntry } from '#utils/useHoveredEntry'
import { makeTireEditorLocation } from '../components/ConfigurationNavigation'
import { useOptionalEquipment } from '../OptionalEquipment/useOptionalEquipment'
import type { OnHistoryChangeClicked } from '../Summary/Accordion/SidebarHistoryList'
import { makeFamilyMetaData, makeGroupMetaData, makeVariantMetaData } from '../utils/utils'
import { type FilterState, hasFilter } from './Filter/FilterState'
import type { DetailsRow, FamilyDetailsRow, TableStatistics } from './types'

export type DetailsContext = ReturnType<typeof useDetailsContext>

const useSelectVariantForFamily = (configurationId: LocalConfigurationId, enrichment: ConfigurationEnrichmentData, dispatch: AppDispatch) => {
  const isNationalCode = useCallback(
    (familyId: EnhancedProductFamilyId) => {
      const family = enrichment.enrichedFamilies[familyId]
      if (!family) {
        return false
      }
      return family.source === 'national-code'
    },
    [enrichment.enrichedFamilies],
  )

  const nationalCodeSetter = useCallback(
    (familyId: EnhancedProductFamilyId, variantId: EnhancedProductVariantId | undefined) => {
      console.log('Setting national code for family', familyId, variantId)
      dispatch(setVariantForNationalCodeFamily({ configurationId, familyId, toVariantId: variantId }))
    },
    [dispatch, configurationId],
  )

  const ecmSetter = useCallback(
    (familyId: EnhancedProductFamilyId, variantId: EnhancedProductVariantId | undefined) => {
      dispatch(
        setVariantsForFamily({
          configurationId,
          selections: [
            {
              familyId: familyId,
              toVariantId: variantId,
            },
          ],
        }),
      )
    },
    [dispatch, configurationId],
  )

  return useCallback(
    (familyId: EnhancedProductFamilyId, variantId: EnhancedProductVariantId | undefined) => {
      isNationalCode(familyId) ? nationalCodeSetter(familyId, variantId) : ecmSetter(familyId, variantId)
    },
    [ecmSetter, nationalCodeSetter, isNationalCode],
  )
}

const useToggleLock = (configurationId: LocalConfigurationId, dispatch: AppDispatch) => {
  return useCallback(
    (familyId: EnhancedProductFamilyId) => {
      dispatch(toggleLocked({ id: configurationId, family: familyId }))
    },
    [dispatch, configurationId],
  )
}

const useFavoriteRow = (dispatch: AppDispatch) => {
  return useCallback(
    (favoriteParams: Parameters<typeof toggleFavorite>[0]) => {
      dispatch(toggleFavorite(favoriteParams))
    },
    [dispatch],
  )
}

const useControlHighlight = (dispatch: AppDispatch, configuration: TruckConfiguration) => {
  const isVariantHighlighted = useCallback(
    (familyId: EnhancedProductFamilyId, variantId: EnhancedProductVariantId | undefined) =>
      variantId &&
      familyId &&
      (configuration.viewHighlight.modal?.familyId === familyId || configuration.viewHighlight.panel?.familyId === familyId) &&
      (configuration.viewHighlight.modal?.variantId === variantId || configuration.viewHighlight.panel?.variantId === variantId),
    [configuration.viewHighlight],
  )
  const isVariantInPanel = useCallback(
    (familyId: EnhancedProductFamilyId, variantId: EnhancedProductVariantId | undefined) =>
      variantId && familyId && configuration.viewHighlight.panel?.familyId === familyId && configuration.viewHighlight.panel?.variantId === variantId,
    [configuration.viewHighlight.panel],
  )
  const isFamilyHighlighted = useCallback(
    (familyId: EnhancedProductFamilyId) =>
      configuration.viewHighlight.modal?.familyId === familyId || configuration.viewHighlight.panel?.familyId === familyId,
    [configuration.viewHighlight],
  )
  const showHighlight = useCallback(
    (scope: 'modal' | 'panel', familyId: EnhancedProductFamilyId, variantId: EnhancedProductVariantId) => {
      dispatch(
        addViewHighlight({
          id: configuration.id,
          familyId,
          variantId,
          stage: configuration.step.stage,
          scope: scope,
        }),
      )
    },
    [dispatch, configuration.id, configuration.step.stage],
  )

  const clearHighlight = useCallback(
    (scope: 'modal' | 'panel') => {
      dispatch(clearViewHighlight({ id: configuration.id, scope: scope }))
    },
    [dispatch, configuration.id],
  )

  return {
    showHighlight,
    clearHighlight,
    isFamilyHighlighted,
    isVariantHighlighted,
    isVariantInPanel,
  }
}

export const useSetPrintOptions = (dispatch: AppDispatch, configurationId?: LocalConfigurationId) => {
  return useCallback(
    (printOptions: PrintOptions, familyId: EnhancedProductFamilyId, variantId?: EnhancedProductVariantId) => {
      dispatch(
        setPrintOptions({
          id: configurationId,
          options: {
            family: familyId,
            variant: variantId,
            printOptions: printOptions,
          },
        }),
      )
    },
    [dispatch, configurationId],
  )
}

export const useToggleExpandPrintColumns = (dispatch: AppDispatch) => {
  return [
    useCallback(() => {
      dispatch(toggleExpandPrintColumns())
    }, [dispatch]),
    useAppSelector((x) => x.localConfig.printConfig.expandPrintColumns),
  ] as const
}

export const usePrintPrompt = (shouldPrint: boolean) => {
  const [debounce, setDebounce] = useState(false)
  useEffect(() => {
    if (debounce || !shouldPrint) {
      return
    }
    // delaying the print prompt so the browser can catch up with an initial layouting and routing
    const timeout = setTimeout(() => {
      window.print()
      setDebounce(true)
      window.history.replaceState({}, '')
    }, 250)
    return () => {
      clearTimeout(timeout)
    }
  }, [shouldPrint, debounce])
}

export const useAnyPrintPreview = (displayedRows: DetailsRow[]) => {
  return useMemo(() => {
    for (const row of displayedRows) {
      if (row.type !== 'family') {
        continue
      }
      const variant = row.meta.fallbackVariant
      if (variant?.printOptions?.doNotPrint) {
        continue
      }

      // this is not completely correct here, it would be required to fetch a cosima document first and see if there is any preview even
      if (variant?.printOptions?.previewImage && row.meta.fallbackVariant?.printOptions?.previewImage) {
        return true
      }
    }
    return false
  }, [displayedRows])
}

const allSubgroups = (group: AppProductGroup): AppProductGroup[] => [group, ...(group.subGroups ?? []).flatMap((x) => allSubgroups(x))]

export const useLinearizeDetailsTable = (
  configuration: TruckConfiguration,
  enrichment: ConfigurationEnrichmentData,
  filter: { operators: Filter[]; operation: 'AND' | 'OR' },
  expandedFamily: EnhancedProductFamilyId | undefined,
  expandedFamilies: Set<EnhancedProductFamilyId>,
) => {
  const productGroups = useMemo(() => {
    return enrichment.rootGroups.filter((x) => !!x)
  }, [enrichment.rootGroups])

  const filterStatus = useMemo(() => filterProductGroups(productGroups, filter.operators, filter.operation), [productGroups, filter])

  const displayedRows = useMemo(() => {
    const results: DetailsRow[] = []

    for (const group of productGroups) {
      // pull out all groups of that group - including itself
      const allGroups = allSubgroups(group)
      for (const subGroup of allGroups) {
        if (filterStatus[subGroup.path] === 'filtered') {
          continue
        }
        if (subGroup.parentGroup) {
          results.push({
            type: 'subgroup',
            id: subGroup.path,
            data: subGroup as AppProductGroup & { parentGroup: AppProductGroup },
          })
        } else {
          results.push({
            type: 'group',
            id: group.path,
            data: group,
            meta: makeGroupMetaData(group),
          })
        }

        for (const family of subGroup.families) {
          if (filterStatus[family.path] === 'filtered') {
            continue
          }
          const isExpandedByFamily = expandedFamily === family.id
          const isExpandedBySearch = expandedFamilies.has(family.id)
          const expansionReason = isExpandedByFamily ? 'by-family-expansion' : isExpandedBySearch ? 'by-search-expansion' : false

          results.push({
            type: 'family',
            id: family.path,
            data: family,
            meta: makeFamilyMetaData(family, false, expansionReason, configuration),
          })

          if (expansionReason) {
            for (const variant of family.variants) {
              if (filterStatus[variant.path] === 'filtered') {
                continue
              }
              // skip the variant that is shown in the family row anyway (so no dupes)
              if (variant.id === family.variants[0]?.id) {
                continue
              }

              results.push({
                type: 'variant',
                id: variant.path,
                data: variant,
                meta: makeVariantMetaData(variant, false, expansionReason, configuration),
              })
            }
          }
        }
      }
    }

    return results
  }, [productGroups, configuration, filterStatus, expandedFamily, expandedFamilies])

  return {
    filterStatus,
    productGroups,
    displayedRows,
  }
}

export const usePrintDropdown = () => {
  const location = useRef<HTMLElement>(null)
  const [open, setOpen] = useState<
    | {
        open: false
        family?: undefined
        variant?: undefined
      }
    | { open: true; family: EnhancedProductFamilyId; variant: EnhancedProductVariantId }
  >({ open: false })

  const showPrintViewAt = useCallback((where: HTMLElement, family: EnhancedProductFamilyId, variant: EnhancedProductVariantId) => {
    location.current = where
    setOpen({
      open: true,
      family: family,
      variant: variant,
    })
  }, [])

  const closePrintView = useCallback(() => {
    setOpen({ open: false })
  }, [])

  return {
    location,
    isOpen: open.open,
    familyId: open.family,
    variantId: open.variant,
    showPrintViewAt,
    closePrintView,
  }
}

export const useConfigurationHoveredEntry = (
  configuration: TruckConfiguration | undefined,
  enrichment: ConfigurationEnrichmentData | undefined,
  dispatch: AppDispatch,
  view: keyof Exclude<TruckConfiguration['viewDetails'], undefined>,
) => {
  const { updateHoveredEntry, eventBus } = useHoveredEntry<ProductPath>({
    delay: 500,
    onHoveredEntry: (path) => {
      if (!configuration) {
        return
      }
      dispatch(
        setScrollPosition({
          path: path,
          source: '',
          view: view,
          id: configuration?.id,
        }),
      )
    },
  })

  const onEntryHovered = useCallback(
    (_evt: React.MouseEvent<HTMLDivElement>, change: Removal | Addition | Change) => {
      const path = enrichment?.enrichedFamilies[change.family.id]?.path
      if (path && (change.archetype === 'change' || change.archetype === 'addition')) {
        updateHoveredEntry(path)
      }
    },
    [enrichment?.enrichedFamilies, updateHoveredEntry],
  )

  return {
    eventBus,
    updateHoveredEntry,
    onEntryHovered,
  }
}

export const useFilterState = (
  configuration: TruckConfiguration,
  enrichment: ConfigurationEnrichmentData,
  view: ViewDetailKey,
  initiallyShowPrint: boolean,
) => {
  const hiddenGroups = useAppSelector((x) => x.truckConfigurations.meta.hiddenGroups)
  const [filterState, setFilterStateInternal] = useState<FilterState>({
    showPrint: initiallyShowPrint,
    collapseGroup: configuration.viewDetails?.[view]?.collapsedGroups,
  })

  const setFilterStateExternal = useCallback((newFilterState: FilterState) => {
    setFilterStateInternal((value) => {
      const hadExpansionRulesChanged = value.showEdited || value.showFavorites || value.showInsights || value.showPrint
      const expansionRulesChanged =
        newFilterState.showEdited || newFilterState.showFavorites || newFilterState.showInsights || newFilterState.showPrint
      const changesExpansionRules = hadExpansionRulesChanged !== expansionRulesChanged
      return { ...value, ...newFilterState, expandFamily: changesExpansionRules ? undefined : newFilterState.expandFamily }
    })
  }, [])

  const setSearchQuery = useCallback(
    (searchQuery: string | undefined) => {
      setFilterStateInternal({ ...filterState, search: searchQuery })
    },
    [filterState],
  )
  const withFilter = hasFilter(filterState)
  const collapsedGroups = new Set(filterState.collapseGroup)
  const operators = withFilter
    ? makeDefaultFilterOperators({
        hiddenGroups,
        searchText: filterState.search,
        includeOnlySelectedVariants: view === 'print',
        hasInsights: filterState.showInsights,
        hasEdits: filterState.showEdited,
        hasFavorites: filterState.showFavorites,
        hasPrintOptions: filterState.showPrint,
        displayFamilies: filterState.displayFamilies,
        collapseProductGroups: collapsedGroups,
        displayGroupsWithFamilies: filterState.displayGroup,
      })
    : [makeGroupNotPartOfWellKnownOperator(hiddenGroups), makeGroupIsCollapsedOperator(collapsedGroups)]

  const allSubgroupsCollapsed = useMemo(() => {
    if (filterState.collapseGroup === undefined || filterState.collapseGroup.length <= 0) {
      return false
    }

    for (const group of configuration.productGroups) {
      if (filterState.collapseGroup.includes(group.id)) {
        continue
      }
      for (const subgroup of group.subGroups ?? []) {
        if (!filterState.collapseGroup.includes(subgroup.id)) {
          return false
        }
      }
    }
    return true
  }, [configuration.productGroups, filterState])

  const expandedFamilies = useMemo(() => {
    const getSubGroups = (group: EnhancedProductGroup): EnhancedProductGroup[] => [group, ...(group.subGroups ?? []).flatMap((x) => getSubGroups(x))]

    const set = new Set<EnhancedProductFamilyId>()
    if (view === 'print') {
      return set
    }

    if (filterState.expandFamily) {
      set.add(filterState.expandFamily)
    }

    // TODO think about aligning this with the filter step, this will do some of the work again
    if (filterState.search) {
      const query = filterState.search.toLowerCase()
      for (const variant of Object.values(enrichment.enrichedVariants).filter((x) => !!x)) {
        // expand families if a variant is found but would not be visible
        const variantFound = !variant.isSelected && productVariantContainsString(variant.parentFamily, variant, query)
        if (variantFound) {
          set.add(variant.parentFamily.id)
        }
      }
    }

    if (filterState.showFavorites) {
      for (const family of Object.values(enrichment.enrichedFamilies).filter((x) => !!x)) {
        for (const variant of family.variants) {
          if (!variant.isFavorite) {
            continue
          }
          // expand families if a variant is favorited but would not be visible
          if (!variant.isSelected) {
            set.add(family.id)
            break
          }

          // keep family expanded when the favorited variant is no the first entry
          // because it may still be selected but not the first family entry anymore
          if (filterState.expandFamily === family.id && family.variants[0].id !== variant.id) {
            set.add(family.id)
            break
          }
        }
      }
    }

    if (filterState.showInsights) {
      for (const variant of Object.values(enrichment.enrichedVariants).filter((x) => !!x)) {
        // expand families if a variant has insights but would not be visible
        const variantFound = !variant.isSelected && productVariantHasInsights(variant)
        if (variantFound && !variant.isSelected) {
          set.add(variant.parentFamily.id)
        }
      }
    }

    return set
  }, [
    enrichment.enrichedFamilies,
    enrichment.enrichedVariants,
    filterState.search,
    filterState.showFavorites,
    filterState.showInsights,
    filterState.expandFamily,
    view,
  ])

  const searchTerms = useMemo(() => {
    if (filterState.search) {
      const terms = matchCodeGroups(filterState.search)
      if (!terms.possibleCode) {
        return [filterState.search]
      }
      return [filterState.search, terms.groups.code, terms.groups.variant]
    }
    return filterState.search ? [filterState.search] : []
  }, [filterState.search])

  const canBeClosed = useCallback(
    (entry: DetailsRow) => {
      if (entry.type === 'variant' || entry.type === 'family') {
        const familyId = entry.type === 'variant' ? entry.data.parentFamily.id : entry.data.id
        if (expandedFamilies.has(familyId)) {
          return filterState.expandFamily !== undefined && filterState.expandFamily === familyId
        }
        return false
      }
      return entry.data.subGroups.length > 0 || entry.data.families.length > 0
    },
    [expandedFamilies.has, filterState.expandFamily],
  )

  const canBeExpanded = useCallback((entry: DetailsRow) => {
    if (entry.type === 'variant') {
      return false
    }

    if (entry.type === 'family') {
      return entry.data.variants.length > 1
    }

    return entry.data.subGroups.length > 0 || entry.data.families.length > 0
  }, [])

  return {
    filterState,
    isFilterSet: hasFilter(filterState, view),
    filterOperators: operators,
    expandedFamilies,
    canBeExpanded,
    canBeClosed,
    setFilterState: setFilterStateExternal,
    setFilterStateInternal,
    setSearchQuery,
    allSubgroupsCollapsed,
    searchTerms,
  }
}

export const useMarkerHighlight = (
  filterState: FilterState,
  rows: DetailsRow[],
  configuration: TruckConfiguration,
  isHighlightingEnabled: boolean,
) => {
  const [expansionCache, setExpansionCache] = useState<{ lastExpandedFamily: EnhancedProductFamilyId; currentlyExpanded: boolean } | undefined>(
    undefined,
  )

  useEffect(() => {
    const expandFamily = filterState.expandFamily
    if (expandFamily === undefined) {
      setExpansionCache((val) => {
        if (val) {
          return { ...val, currentlyExpanded: false }
        }
        return val
      })
      return
    }
    setExpansionCache({ lastExpandedFamily: expandFamily, currentlyExpanded: true })
  }, [filterState.expandFamily])

  return useMemo((): {
    advancedHighlighting: boolean
    markers: EntryHighlight[]
    expandedIndex: number | undefined
    recentlyExpanded: EnhancedProductFamilyId | undefined
    recentlyAffected: Set<EnhancedProductFamilyId>
    recentlyChanged: EnhancedProductFamilyId | undefined
    getRowStatus: (
      row: FamilyDetailsRow,
    ) => 'currently-open' | 'recently-edited' | 'recently-affected' | 'recently-opened' | 'recently-added' | 'none'
  } => {
    const lastChange = configuration.history?.findLast((x) => (x.type === 'change' || x.type === 'redo') && x.status === 'success')
    const selectionIndex = rows.findIndex((row) => lastChange?.selections.findIndex((s) => s.family.id === row.data.id))
    const selection = lastChange?.selections[selectionIndex]
    const [changeIndex, changeId, changeTitle] = lastChange
      ? [rows.findIndex((row) => selection?.family.id === row.data.id), selection?.family.id, selection?.family.title]
      : [-1, undefined, undefined]
    const affectedIndices =
      lastChange?.consequences.changes
        .map((x) => [rows.findIndex((row) => row.data.id === x.family.id), x.family.id, x.family.title] as const)
        .filter((x) => !!x[0]) ?? []
    const lastOpenedIndex = expansionCache ? rows.findIndex((row) => row.data.id === expansionCache.lastExpandedFamily) : -1
    const lastOpenedTitle = lastOpenedIndex < 0 ? undefined : rows[lastOpenedIndex].data.title
    const expanded = expansionCache?.currentlyExpanded ?? false

    // the abbreviated version is that the feature flag only inserts one marker - which is of the opened entry.
    if (!isHighlightingEnabled) {
      // indicator of the last family someone has been opened/closed
      const markers: EntryHighlight[] =
        lastOpenedIndex < 0 ? [] : [{ index: lastOpenedIndex, type: 'highlight', title: `Opened family (${lastOpenedTitle})`, zIndex: 0 }]
      return {
        advancedHighlighting: isHighlightingEnabled,
        markers,
        expandedIndex: lastOpenedIndex < 0 ? undefined : lastOpenedIndex,
        recentlyExpanded: undefined,
        recentlyAffected: new Set<EnhancedProductFamilyId>(),
        recentlyChanged: undefined,
        getRowStatus: () => 'none',
      }
    }

    const markers: EntryHighlight[] = [
      // the family that has been changed last
      changeIndex < 0
        ? undefined
        : ({
            index: changeIndex,
            type: 'important',
            dotted: true,
            title: `Last changed family (${changeTitle})`,
            zIndex: 2,
          } satisfies EntryHighlight),
      // all the families that have been changed as consequence of the last family change
      ...affectedIndices.map(
        ([index, _id, title]): EntryHighlight => ({
          index,
          type: 'highlight',
          dotted: true,
          title: `Affected family by last change (${title})`,
          zIndex: 1,
        }),
      ),
      // indicator of the last family someone has been opened/closed
      lastOpenedIndex < 0 || lastOpenedIndex === changeIndex
        ? undefined
        : ({
            index: lastOpenedIndex,
            type: expanded ? 'important' : 'info',
            title: expanded ? `Opened family (${lastOpenedTitle})` : `Recently opened family (${lastOpenedTitle})`,
            zIndex: 0,
          } satisfies EntryHighlight),
    ].filter((x) => !!x)

    const recentlyAffected = new Set<EnhancedProductFamilyId>(affectedIndices.map((x) => x[1]))
    const recentlyAdded = new Set<EnhancedProductFamilyId>(lastChange?.consequences.additions.map((x) => x.family.id) ?? [])

    const getRowStatus = (row: FamilyDetailsRow) => {
      if (expansionCache?.lastExpandedFamily === row.data.id && expansionCache?.currentlyExpanded) {
        return 'currently-open'
      }
      if (changeId === row.data.id) {
        return 'recently-edited'
      }
      if (recentlyAffected.has(row.data.id)) {
        return 'recently-affected'
      }
      if (recentlyAdded.has(row.data.id)) {
        return 'recently-added'
      }
      if (expansionCache?.lastExpandedFamily === row.data.id) {
        return 'recently-opened'
      }
      return 'none'
    }

    return {
      advancedHighlighting: isHighlightingEnabled,
      markers,
      expandedIndex: lastOpenedIndex < 0 ? undefined : lastOpenedIndex,
      recentlyExpanded: !expansionCache || lastOpenedIndex < 0 ? undefined : expansionCache.lastExpandedFamily,
      recentlyAffected: new Set<EnhancedProductFamilyId>(affectedIndices.map((x) => x[1])),
      recentlyChanged: changeId,
      getRowStatus,
    }
  }, [expansionCache, rows, isHighlightingEnabled, configuration.history])
}

export const useTableStatistics = (
  filterResult: ReturnType<typeof useLinearizeDetailsTable>['filterStatus'],
  linearizedData: DetailsRow[],
  enrichment: ConfigurationEnrichmentData,
) => {
  return useMemo(() => {
    const unfilteredKeys = Object.keys(filterResult)
      .filter((x): x is ProductPath => !!x)
      .filter((x) => filterResult[x] === 'unfiltered')

    let totalGroups = 0
    let totalFamilies = 0
    let totalVariants = 0
    for (const key of unfilteredKeys) {
      const element = enrichment.byPath[key]
      if (isProductGroup(element)) {
        totalGroups += 1
        continue
      }
      if (isProductFamily(element)) {
        totalFamilies += 1
        continue
      }
      totalVariants += 1
    }

    const result: TableStatistics = {
      totalRows: unfilteredKeys.length,
      totalGroups,
      totalFamilies,
      totalVariants,
      variantsBelowByPath: {},
      familiesBelowByPath: {},
    }

    const bucket = filterResult
    for (const row of linearizedData) {
      if (bucket[row.data.path] !== 'unfiltered') {
        continue
      }
      if (isProductGroup(row.data)) {
      } else if (isProductFamily(row.data)) {
        const paths = getHierarchyPaths(row.data)
        for (const path of paths) {
          result.familiesBelowByPath[path] = (result.familiesBelowByPath[path] ?? 0) + 1
        }
      } else if (isProductVariant(row.data)) {
        const paths = getHierarchyPaths(row.data)
        for (const path of paths) {
          result.variantsBelowByPath[path] = (result.variantsBelowByPath[path] ?? 0) + 1
        }
      }
    }

    return result
  }, [filterResult, linearizedData, enrichment])
}

export const useScrollRecovery = (
  configuration: TruckConfiguration | undefined,
  displayedRows: { id: ProductPath }[],
  virtualizer: RefObject<Virtualizer<HTMLDivElement, Element> | null>,
  view: ViewDetailKey,
) => {
  const scrollPosition = configuration?.viewDetails?.[view]?.scrollPosition
  // biome-ignore lint/correctness/useExhaustiveDependencies: only scroll when the configuration is shown first first
  useEffect(() => {
    if (scrollPosition?.path) {
      const index = displayedRows.findIndex((x) => x.id === scrollPosition?.path)
      requestAnimationFrame(() => {
        virtualizer.current?.scrollToIndex(index - 1, { align: 'start' })
      })
    }
  }, [configuration?.id])
}

export const useCollapse = (
  dispatch: AppDispatch,
  configuration: TruckConfiguration,
  view: ViewDetailKey,
  displayedRows: { id: ProductPath }[],
  virtualizer: RefObject<Virtualizer<HTMLDivElement, Element> | null>,
  filterState: ReturnType<typeof useFilterState>['filterState'],
  setFilterStateInternal: ReturnType<typeof useFilterState>['setFilterStateInternal'],
  expandedFamilies: ReturnType<typeof useFilterState>['expandedFamilies'],
) => {
  const [lastActiveFamily, setLastActiveFamily] = useState<ProductPath>()

  /**
   * * Toggle and stores collapse information for groups and subgroups
   * * Toggle collapse of families
   * * Closes the parent family of a variant
   */
  const toggleCollapse = useCallback(
    (entry: DetailsRow) => {
      {
        // in here, figure out the location of the parent family
        const path = entry.type === 'variant' ? entry.data.parentFamily.path : entry.type === 'family' ? entry.data.path : undefined
        const index = path && displayedRows.findIndex((x) => x.id === path)
        if (index !== undefined && index >= 0 && virtualizer.current) {
          setLastActiveFamily(path)
          const { startIndex, endIndex } = virtualizer.current.calculateRange() ?? {}
          if (startIndex && endIndex && (startIndex > index || endIndex < index)) {
            const align = startIndex > index ? 'start' : 'end'
            const offset = startIndex > index ? -1 : +2
            requestAnimationFrame(() => {
              virtualizer.current?.scrollToIndex(index + offset, { align: align, behavior: 'auto' })
            })
          }
        }
      }

      if (entry.type === 'variant') {
        setFilterStateInternal((state) => ({ ...state, expandFamily: undefined }))
        return
      }
      if (entry.type === 'family') {
        setFilterStateInternal((state) => ({ ...state, expandFamily: state.expandFamily === entry.data.id ? undefined : entry.data.id }))
        return
      }

      const { id } = entry.data
      if (filterState.collapseGroup?.includes(id)) {
        const collapsedGroups = filterState.collapseGroup.filter((x) => x !== id)
        setFilterStateInternal({ ...filterState, collapseGroup: collapsedGroups })
        dispatch(setCollapsedGroups({ id: configuration.id, collapsedGroups, view }))
      } else {
        const collapsedGroups = [...(filterState.collapseGroup ?? []), id]
        setFilterStateInternal({ ...filterState, collapseGroup: collapsedGroups })
        dispatch(setCollapsedGroups({ id: configuration.id, collapsedGroups, view }))
      }
    },
    [filterState, configuration.id, view, dispatch, displayedRows.findIndex, virtualizer.current, setFilterStateInternal],
  )

  const collapseAll = useCallback(
    (collapse: 'collapse' | 'uncollapse') => {
      if (collapse === 'uncollapse') {
        // when uncollapsing all groups and subgroups uncollapse
        setFilterStateInternal((state) => ({ ...state, collapseGroup: [] }))
        return
      }

      const collapsedGroups = configuration.productGroups.flatMap((x) => x.subGroups?.map((x) => x.id) ?? [])
      setFilterStateInternal((state) => ({ ...state, collapseGroup: collapsedGroups }))
      dispatch(setCollapsedGroups({ id: configuration.id, collapsedGroups, view }))
    },
    [configuration.id, configuration.productGroups, view, dispatch, setFilterStateInternal],
  )

  /**
   * Is the given row expanded,thus showing its children:
   * * group: return `by-group-expansion` if expanded, showing its subgroups
   * * subgroup: return `by-group-expansion` if expanded, showing its families
   * * family: returns `by-family-expansion` or `by-search-expansion` if expanded, showing its variants
   * * variant: returns `by-family-expansion` or `by-search-expansion` if the parent family is expanded, itself is expanded
   */
  const isExpanded = useCallback(
    (entry: DetailsRow) => {
      if (entry.type === 'group' || entry.type === 'subgroup') {
        return !filterState.collapseGroup?.includes(entry.data.id) ? 'by-group-expansion' : false
      }

      const familyId = entry.type === 'family' ? entry.data.id : entry.data.parentFamily.id
      return filterState.expandFamily === familyId ? 'by-family-expansion' : expandedFamilies.has(familyId) ? 'by-search-expansion' : false
    },
    [filterState, expandedFamilies],
  )
  return {
    toggleCollapse,
    collapseAll,
    isExpanded,
    lastActiveFamily,
  }
}

// primary idea is to adding hooks for all necessary features and thin them so much, that they're accessible through enrichment
// meaning: the modal feature needs to know the family id to find the family option modal data, but not much more
//
// additionally the context should/could be as bloated, as the individual rows require it, so that the context holds all the functionality
// **once** before each row does the same aggregations many times
export const useDetailsContext = ({
  configuration,
  enrichment = {
    byPath: {},
    enrichedFamilies: {},
    enrichedGroups: {},
    enrichedVariants: {},
    rootGroups: [],
    deltaPriceRange: { minDeltaPrice: -1, maxDeltaPrice: 1, normalizationFactor: 1 },
  },
  view: externalView,
}: {
  configuration: TruckConfiguration
  enrichment: ConfigurationEnrichmentData | undefined
  view?: ViewDetailKey
}) => {
  const [internalView, setView] = useState<ViewDetailKey>(externalView ?? 'details')
  const view = externalView ?? internalView

  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const selectVariantForFamily = useSelectVariantForFamily(configuration.id, enrichment, dispatch)
  const highlightingEnabled = useTableChangeHighlightEnabled()
  const toggleLocked = useToggleLock(configuration.id, dispatch)
  const favoriteRow = useFavoriteRow(dispatch)
  const setPrintOptions = useSetPrintOptions(dispatch, configuration.id)
  const [toggleExpandPrintColumns, expandedPrintColumn] = useToggleExpandPrintColumns(dispatch)
  const highlight = useControlHighlight(dispatch, configuration)
  const printDropdown = usePrintDropdown()
  const hoveredEntry = useConfigurationHoveredEntry(configuration, enrichment, dispatch, view)
  const { filterOperators, ...filter } = useFilterState(configuration, enrichment, view, false)
  const equipment = useOptionalEquipment(dispatch, configuration, enrichment, t)

  const virtualizer = useRef<Virtualizer<HTMLDivElement, Element>>(null)
  const { productGroups, displayedRows, filterStatus } = useLinearizeDetailsTable(
    configuration,
    enrichment,
    {
      operators: filterOperators,
      operation: 'OR',
    },
    filter.filterState.expandFamily,
    filter.expandedFamilies,
  )

  const { toggleCollapse, isExpanded, collapseAll, lastActiveFamily } = useCollapse(
    dispatch,
    configuration,
    view,
    displayedRows,
    virtualizer,
    filter.filterState,
    filter.setFilterStateInternal,
    filter.expandedFamilies,
  )
  const statistics = useTableStatistics(filterStatus, displayedRows, enrichment)
  useScrollRecovery(configuration, displayedRows, virtualizer, view)

  const onHistoryEntryClicked = useCallback<OnHistoryChangeClicked>(
    (evt, change) => {
      const path = enrichment?.enrichedFamilies[change.family.id]?.path
      const idx = displayedRows.findIndex((x) => x.data.path === path)
      if (!idx || change.archetype === 'removal') {
        return
      }
      evt.stopPropagation()
      virtualizer.current?.scrollToIndex(idx, { align: 'center' })
    },
    [displayedRows.findIndex, enrichment?.enrichedFamilies],
  )

  const { markers, ...highlightingInfos } = useMarkerHighlight(filter.filterState, displayedRows, configuration, highlightingEnabled)
  useEffect(() => {
    const family = filter.filterState.expandFamily && enrichment.enrichedFamilies[filter.filterState.expandFamily]
    if (family) {
      /*
      // does not need to rerun compatibility checks if the focus group is already selected
      if (
        configuration.compatibility?.focusGroup === family.parentGroup.id ||
        configuration.compatibility?.focusGroup === family.parentGroup.parentGroup?.id
      ) {
        return
      }
        */
      dispatch(checkGroupCompatibility({ id: configuration.id, groupId: family.parentGroup.id }))
    }
  }, [filter.filterState.expandFamily, enrichment.enrichedFamilies, configuration.id, dispatch])

  const location = useLocation()
  usePrintPrompt(location.state && typeof location.state === 'object' && location.state.withPrintPrompt === true)
  const anyPrintPreview = useAnyPrintPreview(displayedRows)

  const scrollToPath = useCallback(
    (path: ProductPath, alwaysScroll: boolean = false) => {
      const index = displayedRows.findIndex((x) => x.data.path === path)
      if (index < 0 || !virtualizer.current) {
        return
      }
      const range = virtualizer.current.calculateRange()
      const offset = virtualizer.current.getOffsetForIndex(index - 1, 'start')
      if (!offset) {
        return
      }

      hoveredEntry.updateHoveredEntry(path)
      if (alwaysScroll || (range && (range.startIndex > index || range.endIndex < index))) {
        // only scroll, when the path is currently not visible to avoid confusion by jumping around the table
        virtualizer.current.scrollToOffset(offset[0], { align: offset[1], behavior: 'auto' })
      }
    },
    [displayedRows, hoveredEntry],
  )

  const { inView, navigate } = useAppNavigation()
  const onSidebarEntryClicked = useCallback(
    (_evt: React.MouseEvent<HTMLElement, MouseEvent>, path: ProductPath) => {
      if (!inView('configuration/step-2', 'configuration/step-2/print', 'configuration/step-3')) {
        navigate(appRoutes['configuration/step-2'].make({ configuration: configuration }))
      }
      // enforce scrolling, that makes more sense when clicking titles in the sidebar
      scrollToPath(path, true)
    },
    [scrollToPath, inView, navigate, configuration],
  )

  const onTireEditorClicked = useCallback(() => {
    navigate(makeTireEditorLocation(configuration))
  }, [navigate, configuration])

  // eventually pull all event callbacks out and add them into something more tidy like { actions: { favoriteRow, toggleLocked, ... } } ?
  return {
    i18n,
    t,
    dispatch,
    configuration,
    enrichment,

    view,
    setView,

    markers,
    highlightingInfos,

    productGroups,
    displayedRows,
    filterResult: filterStatus,

    selectVariantForFamily,
    toggleCollapse,
    collapseAll,
    isExpanded,

    scrollToPath,

    toggleLocked,
    favoriteRow,
    highlight,
    setPrintOptions,
    toggleExpandPrintColumns,
    expandedPrintColumn,
    virtualizer,
    printDropdown,
    equipment,
    hoveredEntry,
    filter,
    statistics,
    lastActiveFamily,

    onHistoryEntryClicked,
    onSidebarEntryClicked,
    onTireEditorClicked,

    anyPrintPreview,
  }
}
